/**
 * @file api-notification-templates.ts
 * @description API-клиент шаблонов уведомлений магазина (phase10.5 шаг 4).
 *
 * Endpoints под `/admin/shop/notification-templates/*`. RBAC проверяется в API —
 * SHOP_MANAGE_SETTINGS на всё. apiFetch уже разворачивает `{data}` обёртку.
 */

import { apiFetch } from "./api-client";

// ─── Types ──────────────────────────────────────────────────────────────────

export type NotificationKind =
  | "ORDER_CREATED"
  | "ORDER_PAID"
  | "ORDER_PACKED"
  | "SHIPMENT_SENT"
  | "SHIPMENT_DELIVERED"
  | "PICKUP_READY"
  | "RETURN_REQUESTED"
  | "RETURN_REFUNDED"
  | "DEAL_APPROVAL_REQUEST"
  /* phase19.15 (F30) — «доставка по договорённости». */
  | "DELIVERY_AGREEMENT_REQUESTED"
  | "DELIVERY_AGREEMENT_CALCULATED"
  | "DELIVERY_AGREEMENT_CANCELLED"
  | "CUSTOMER_INVITE"
  | "CUSTOMER_AUTO_INVITE"
  | "PASSWORD_RESET"
  | "REVIEW_REQUEST"
  | "MARKETING_PROMO";

export type NotificationChannel = "EMAIL" | "SMS" | "TELEGRAM" | "IN_APP";

export interface NotificationTemplateDto {
  id: string;
  shopId: string;
  kind: NotificationKind;
  channel: NotificationChannel;
  locale: string;
  subject: string | null;
  body: string;
  isSystem: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationTemplateListResult {
  items: NotificationTemplateDto[];
  total: number;
}

export interface CreateNotificationTemplatePayload {
  kind: NotificationKind;
  channel: NotificationChannel;
  locale?: string;
  subject?: string | null;
  body: string;
  isActive?: boolean;
}

export interface UpdateNotificationTemplatePayload {
  subject?: string | null;
  body?: string;
  isActive?: boolean;
}

// ─── API ────────────────────────────────────────────────────────────────────

export function listNotificationTemplates(query?: {
  kind?: NotificationKind;
  channel?: NotificationChannel;
}): Promise<NotificationTemplateListResult> {
  const params = new URLSearchParams();
  if (query?.kind) params.set("kind", query.kind);
  if (query?.channel) params.set("channel", query.channel);
  const qs = params.toString();
  return apiFetch<NotificationTemplateListResult>(
    `/admin/shop/notification-templates${qs ? `?${qs}` : ""}`,
  );
}

export function getNotificationTemplate(
  id: string,
): Promise<NotificationTemplateDto> {
  return apiFetch<NotificationTemplateDto>(
    `/admin/shop/notification-templates/${id}`,
  );
}

export function createNotificationTemplate(
  payload: CreateNotificationTemplatePayload,
): Promise<NotificationTemplateDto> {
  return apiFetch<NotificationTemplateDto>(
    "/admin/shop/notification-templates",
    { method: "POST", json: payload },
  );
}

export function updateNotificationTemplate(
  id: string,
  payload: UpdateNotificationTemplatePayload,
): Promise<NotificationTemplateDto> {
  return apiFetch<NotificationTemplateDto>(
    `/admin/shop/notification-templates/${id}`,
    { method: "PATCH", json: payload },
  );
}

export function removeNotificationTemplate(id: string): Promise<void> {
  return apiFetch<void>(`/admin/shop/notification-templates/${id}`, {
    method: "DELETE",
  });
}

/** phase19 — рендер шаблона с тестовыми данными (несохранённые правки тоже). */
export function previewNotificationTemplate(payload: {
  subject?: string;
  body: string;
}): Promise<{ subject: string | null; body: string }> {
  return apiFetch<{ subject: string | null; body: string }>(
    "/admin/shop/notification-templates/preview",
    { method: "POST", json: payload },
  );
}

// ─── Labels ────────────────────────────────────────────────────────────────

export const NOTIFICATION_KIND_LABELS: Record<NotificationKind, string> = {
  ORDER_CREATED: "Заказ принят",
  ORDER_PAID: "Заказ оплачен",
  ORDER_PACKED: "Заказ упакован",
  SHIPMENT_SENT: "Заказ отправлен",
  SHIPMENT_DELIVERED: "Заказ доставлен",
  PICKUP_READY: "Готов к самовывозу",
  RETURN_REQUESTED: "Заявка на возврат принята",
  RETURN_REFUNDED: "Возврат средств выполнен",
  DEAL_APPROVAL_REQUEST: "Согласование заказа",
  DELIVERY_AGREEMENT_REQUESTED: "Заявка на согласование доставки принята",
  DELIVERY_AGREEMENT_CALCULATED: "Доставка рассчитана — подтверждение",
  DELIVERY_AGREEMENT_CANCELLED: "Заявка отменена по таймеру",
  CUSTOMER_INVITE: "Инвайт в личный кабинет",
  CUSTOMER_AUTO_INVITE: "Автоинвайт после покупки",
  PASSWORD_RESET: "Сброс пароля",
  REVIEW_REQUEST: "Запрос отзыва",
  MARKETING_PROMO: "Промо-рассылка",
};

export const NOTIFICATION_CHANNEL_LABELS: Record<NotificationChannel, string> = {
  EMAIL: "Email",
  SMS: "SMS",
  TELEGRAM: "Telegram",
  IN_APP: "В личном кабинете",
};

/**
 * Транзакционные kinds — на UI отображаем без значка «требует consent».
 */
export const TRANSACTIONAL_KINDS: Set<NotificationKind> = new Set([
  "ORDER_CREATED",
  "ORDER_PAID",
  "ORDER_PACKED",
  "SHIPMENT_SENT",
  "SHIPMENT_DELIVERED",
  "PICKUP_READY",
  "RETURN_REQUESTED",
  "RETURN_REFUNDED",
  "DEAL_APPROVAL_REQUEST",
  // Письма F30 обслуживают заключаемый договор (ПП РФ 2463 п. 13) — реклама в
  // них запрещена, marketing-consent не требуется (зеркало notification-kinds.ts).
  "DELIVERY_AGREEMENT_REQUESTED",
  "DELIVERY_AGREEMENT_CALCULATED",
  "DELIVERY_AGREEMENT_CANCELLED",
  "CUSTOMER_INVITE",
  "CUSTOMER_AUTO_INVITE",
  "PASSWORD_RESET",
]);
